<style lang="scss" scoped>
.dragging {
  border: 2px solid #2b736b;
  border-radius: 5px;
  background-color: #d8ffed;
  .v-card {
    background-color: #d8ffed;
  }
}
</style>
<template>
  <div>
    <input
      type="file"
      ref="file"
      @change="handleUploadFiles"
      v-show="false"
      accept="image/png, image/jpeg"
      multiple
    />
    <v-card flat>
      <v-card-title>
        <v-icon left color="primary">mdi-image</v-icon> {{ $capitalize($tc("model.gallery_images"))}}
        <v-spacer></v-spacer>
        <v-btn
          class="mx-1"
          @click="openFilesDialog"
          color="primary"
          :disabled="loading_files"
          title="Upload"
          :loading="loading_files"
        >
          <v-icon size="24">mdi-upload</v-icon>
        </v-btn>
        <v-btn
          class="mx-1"
          @click="remote_analysis_dialog = true"
          color="primary"
          :disabled="loading_files"
          :loading="loading_files"
        >
          <v-icon size="24" left>mdi-image-plus</v-icon>{{ $capitalize($tc("model.gallery_remote_analysis"))}}
        </v-btn>
      </v-card-title>
      <div :class="{ dragging: dragging_over_folder === null }">
        <drop
          drop-effect="move"
          @dragenter="dragging_over_folder = null"
          @drop="handleDrop"
        >
          <empty-state
            :when="claim.images.length == 0"
            :message="$capitalize($tc('model.gallery_no_images'))"
            height="400"
          >
            <v-card flat>
              <v-container fluid>
                <v-virtual-scroll
                  :bench="1"
                  :items="chunked_attachments"
                  :item-height="lineHeight"
                  height="400"
                >
                  <template v-slot="{ item }">
                    <v-row dense no-gutters>
                      <v-col
                        v-for="image in item"
                        :key="image.id"
                        class="d-flex child-flex px-1"
                        :cols="numCols"
                      >
                        <v-card
                          tile
                          class="mx-auto"
                          @click="open_file_preview(image)"
                        >
                          <v-img
                            v-if="isFileImage(image.attachment)"
                            :src="getFilePublicUrl(image.attachment)"
                            aspect-ratio="1"
                            class="grey lighten-2"
                            :height="imageHeight"
                          >
                            <template v-slot:placeholder>
                              <v-row
                                class="fill-height ma-0"
                                align="center"
                                justify="center"
                              >
                                <v-progress-circular
                                  indeterminate
                                  color="grey lighten-5"
                                ></v-progress-circular>
                              </v-row>
                            </template>
                          </v-img>
                        </v-card>
                      </v-col>
                    </v-row>
                  </template>
                </v-virtual-scroll>
              </v-container>
            </v-card>
          </empty-state>
        </drop>
      </div>
    </v-card>

    <file-preview-dialog
      v-model="file_preview"
      deletable
      editable
      @change="file_preview = $event"
      @deleteFile="deleteFile(image.id)"
      @handleChangedFile="handleChangedFile"
      :file="image.attachment"
    />

    <select-images-dialog
      v-model="remote_analysis_dialog"
      @change="remote_analysis_dialog = $event"
      :items="lead.remote_analysis_attachments"
      @submit="submitSelectedImages"
    />
  </div>
</template>

<script>
import FilePreviewDialog from "../FilePreviewDialog";
import SelectImagesDialog from "../SelectImagesDialog";
import EmptyState from "../EmptyState";
import { mapState } from "vuex";
import {
  uploadImage,
  deleteImage,
  linkAttachmentsToDamageClaim
} from "@/services/damages";

export default {
  name: "gallery-form",
  components: { FilePreviewDialog, EmptyState, SelectImagesDialog },
  props: {
    claim: Object
  },
  data: () => ({
    folders: [],
    files: [],
    file_preview: false,
    image: {
      attachment: {}
    },
    dragging_over_folder: 0,
    dragging: false,
    file_loading: null,
    file_name: "",
    loading_files: false,
    confirm_file_deletion: false,
    deleting_file: null,
    remote_analysis_dialog: false
  }),
  methods: {
    updateClaimImages(images) {
      this.$store.dispatch("damage_claim/updateImagesDamageClaim", {
        ...this.claim,
        images
      });
    },
    async submitSelectedImages(attachments) {
      this.loading_files = true;
      try {
        this.remote_analysis_dialog = false;
        const response = await linkAttachmentsToDamageClaim(
          this.claim.id,
          attachments
        );
        this.updateClaimImages(this.claim.images.concat(response));
      } catch (e) {
        this.$store.commit("sendMessage", {
          text: e.response.data.message,
          color: "red"
        });
      } finally {
        this.loading_files = false;
      }
    },
    async deleteFile(file_id) {
      this.deleting_file = file_id;
      try {
        await deleteImage(this.claim.id, file_id);
        this.$store.commit("sendMessage", {
          text: "Imagem excluída"
        });
        this.file_preview = false;
        this.updateClaimImages(
          this.claim.images.filter(file => file.id !== file_id)
        );
        this.image = {};
        this.confirm_file_deletion = false;
        this.file_preview = false;
      } catch (error) {
        this.$store.commit("sendMessage", {
          text: "Falha ao excluir arquivo",
          color: "red"
        });
      } finally {
        this.deleting_file = null;
      }
    },
    handleChangedFile(file) {
      this.updateClaimImages(
        this.claim.images.map(attachment => {
          if (attachment.file.id == file.id) {
            return {
              ...attachment,
              file
            };
          }

          return attachment;
        })
      );
    },
    openFilesDialog() {
      this.$refs.file.click();
    },
    async handleUploadFiles($event) {
      this.loading_files = true;
      try {
        for (let file of $event.target.files) {
          await this.delay(1000);
          const response = await this.addFile(file);
          this.updateClaimImages(this.claim.images.concat([response]));
        }
      } catch (e) {
        this.$store.commit("sendMessage", {
          text: e.response.data.message,
          color: "red"
        });
      } finally {
        this.loading_files = false;
      }

      this.$refs.file.value = "";
    },
    async handleDrop(data, event) {
      if (data) {
        if (data.file.category_id !== this.dragging_over_folder) {
          this.file_loading = data.file.id;
          try {
            const response = await this.$http.put(
              "/attachments/" + data.file.id,
              {
                category_id: this.dragging_over_folder
              }
            );

            this.updateClaimImages(
              this.claim.images.map(file => {
                if (file.id === data.file.id) {
                  return { ...file, category_id: response.data.category_id };
                }

                return file;
              })
            );
          } catch (error) {
            this.$store.commit("sendMessage", {
              text: "Não foi possível mover o arquivo",
              color: "red"
            });
          } finally {
            this.file_loading = null;
          }
        }
      }

      this.loading_files = true;
      try {
        for (let file of event.dataTransfer.files) {
          await this.delay(1000);
          const response = await this.addFile(file);
          this.updateClaimImages(this.claim.images.concat([{ ...response }]));
        }
      } catch (e) {
        this.$store.commit("sendMessage", {
          text: e.response.data.message,
          color: "red"
        });
      } finally {
        this.loading_files = false;
      }

      this.dragging_over_folder = 0;
      this.dragging = false;
    },
    delay(delay) {
      return new Promise(resolve => setTimeout(resolve, delay));
    },
    async addFile(file) {
      return await uploadImage(this.claim.id, file);
    },
    handleDragStart() {
      this.dragging = true;
    },
    handleDragEnd() {
      this.dragging = false;
      this.dragging_over_folder = 0;
    },
    isFileImage(file) {
      const image_extensions = ["jpg", "jpeg", "png", "gif"];

      let is_image = false;

      image_extensions.forEach(extension => {
        if (extension === file.extension) {
          is_image = true;
        }
      });

      return is_image;
    },
    async downloadFile(file) {
      window.open(file.public_url);
    },
    getFilePublicUrl(file) {
      switch (file.extension) {
        case "xlsx":
        case "pptx":
        case "docx":
          return this.microsoft_preview(file.public_url);
        default:
          return file.public_url;
      }
    },
    microsoft_preview(public_url) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${public_url}`;
    },
    open_file_preview(image) {
      this.file_preview = true;
      this.image = image;
    },
    async getFolders() {
      const response = await this.$http.get("/attachments_categories");
      this.folders = response.data.map(folder => {
        return {
          ...folder,
          open: true
        };
      });
    }
  },
  computed: {
    lead: function() {
      return this.$store.getters.getLead;
    },
    lineHeight: function() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "120";
        case "sm":
          return "160";
        case "md":
          return "110";
        case "lg":
          return "210";
        case "xl":
          return "210";
      }

      return "200";
    },
    imageHeight: function() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "100";
        case "sm":
          return "150";
        case "md":
          return "100";
        case "lg":
          return "200";
        case "xl":
          return "200";
      }

      return "200";
    },
    numCols: function() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 6;
        case "sm":
          return 4;
        case "md":
          return 3;
        case "lg":
          return 3;
        case "xl":
          return 2;
      }

      return 6;
    },
    itemsPerLine: function() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return 2;
        case "sm":
          return 3;
        case "md":
          return 4;
        case "lg":
          return 4;
        case "xl":
          return 6;
      }

      return 6;
    },
    chunked_attachments: function() {
      const orderedAttachments = this.claim.images;
      orderedAttachments.sort((value, nextValue) => nextValue.id - value.id);

      const groupsOfAttachments = [];

      for (let i = 0; i < orderedAttachments.length; i += this.itemsPerLine) {
        groupsOfAttachments.push(
          orderedAttachments.slice(i, i + this.itemsPerLine)
        );
      }

      return groupsOfAttachments;
    }
  }
};
</script>
